import AlertModal from "components/modals/alert-modal/alert-modal";
import ConfirmModal, { ConfirmModalProps } from "components/modals/confirm-modal/confirm-modal";
import ErrorModal from "components/modals/error-modal/error-modal";
import { renderElement } from "components/modals/modal/modal";
import { handleError, notNil, OperationCancelled } from "core/utils";
import { JSXElementConstructor, MutableRefObject, ReactElement } from "react";
import { Root } from "react-dom/client";
import PagoRechazadoModal from "../../features/private/pagos/components/pago-rechazado.modal";

const rootID = "root";

type RefreshModal<T> = ((props: T) => void) | undefined;

type RefresModalRef<T> = MutableRefObject<RefreshModal<T>>;

export interface ParentRoot {
	modalRoot: Root,
	element: Element | DocumentFragment;
}

export interface ModalProps {
	parent?: ParentRoot;
	onClosed?: VoidFunction;
}

export interface MessageModalProps extends ModalProps {
	message: string;
}

const appendChild = <K extends keyof HTMLElementTagNameMap>(parentId: string) => (tagName: K) => {
	const root = document.getElementById(parentId);
	const child = document.createElement(tagName);

	root?.appendChild(child);
	return child;
};

const apppendChildToRoot = appendChild(rootID);

const unmountComponent = (rootID: string) => (parent?: ParentRoot) => {
	if (notNil(parent)) {
		parent.modalRoot.unmount();

		const root = document.getElementById(rootID);
		root?.removeChild(parent.element);
	}
};

export const createModal = <T extends object>(component: JSXElementConstructor<T>, props: T, ref?: RefresModalRef<T>) => {
	const child = apppendChildToRoot("div");
	const refreshRender = (props: T) => renderElement(child, component, props);

	refreshRender(props);

	ref && (ref.current = refreshRender);
};

const displayModal = (modal: (props: MessageModalProps) => ReactElement<MessageModalProps>) =>
	(message: string) =>
		new Promise<void>(resolve =>
			createModal(modal, { message, onClosed: resolve })
		);

export const displayMessage = displayModal(AlertModal);

export const displayRejected = handleError(displayModal(PagoRechazadoModal));

export const displayError = handleError(displayModal(ErrorModal));

export const displayConfirm = <T>(message: string, param?: T) => {
	const promise = new Promise<T>((resolve: any, reject) => {
		const props: ConfirmModalProps = {
			message,
			acceptText: "Sí",
			cancelText: "No",
			onAccept: () => resolve(param ?? void (0)),
			onCancel: () => reject(new OperationCancelled())
		};
		createModal(ConfirmModal, props);
	});

	return promise;
};

export const unmountComponentFromRoot = unmountComponent(rootID);